import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContentReportService } from '../../../../../services/content-report.service';
import { ContentReport, GroupedReport } from '../../../../../helpers/interface';
import { take } from 'rxjs';

@Component({
  selector: 'app-moderation-reports',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './moderation-reports.html',
  styleUrl: './moderation-reports.css',
})
export class ModerationReports implements OnInit {
  private reportService = inject(ContentReportService);
  private cdr = inject(ChangeDetectorRef);

  openReports: GroupedReport[] = [];
  closedReports: GroupedReport[] = [];
  closedFilter: 'all' | 'reviewed' | 'dismissed' = 'all';
  loading = true;

  ngOnInit(): void {
    this.loadReports();
  }

  loadReports(): void {
    this.loading = true;
    this.reportService
      .getReports({ limit: 500 })
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          const reports = res.reports || [];
          const pending = reports.filter((r) => r.status === 'pending');
          const closed = reports.filter((r) => r.status === 'reviewed' || r.status === 'dismissed');
          this.openReports = this.groupReports(pending);
          this.closedReports = this.groupReports(closed);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Reports API error:', err);
          this.loading = false;
        },
      });
  }

  private groupReports(reports: ContentReport[]): GroupedReport[] {
    const map = new Map<string, GroupedReport>();

    for (const report of reports) {
      const type: 'post' | 'comment' = report.post_id ? 'post' : 'comment';
      const contentId = report.post_id ?? report.comment_id ?? 0;
      const key = `${type}-${contentId}`;

      if (!map.has(key)) {
        let content = '[Content unavailable]';
        if (type === 'post' && report.post) {
          content = report.post.content ?? '[Deleted post]';
        } else if (type === 'comment' && report.comment) {
          content = report.comment.content ?? '[Deleted comment]';
        }

        map.set(key, {
          type,
          contentId,
          content,
          reports: [],
          expanded: false,
          resolvedStatus:
            report.status === 'reviewed' || report.status === 'dismissed'
              ? report.status
              : undefined,
        });
      }

      map.get(key)!.reports.push(report);
    }

    return Array.from(map.values());
  }

  toggleExpanded(group: GroupedReport): void {
    group.expanded = !group.expanded;
  }

  formatReason(reason: string): string {
    return reason.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  getReportDate(report: ContentReport): string {
    return report.createdAt || report.created_at || '';
  }

  get filteredClosedReports(): GroupedReport[] {
    if (this.closedFilter === 'all') return this.closedReports;
    return this.closedReports.filter((g) => g.resolvedStatus === this.closedFilter);
  }

  setClosedFilter(filter: 'all' | 'reviewed' | 'dismissed'): void {
    this.closedFilter = filter;
  }

  markReviewed(group: GroupedReport): void {
    for (const report of group.reports) {
      this.reportService.reviewReport(report.id, 'reviewed').subscribe();
    }
    group.resolvedStatus = 'reviewed';
    this.openReports = this.openReports.filter((g) => g !== group);
    this.closedReports = [group, ...this.closedReports];
  }

  dismiss(group: GroupedReport): void {
    for (const report of group.reports) {
      this.reportService.reviewReport(report.id, 'dismissed').subscribe();
    }
    group.resolvedStatus = 'dismissed';
    this.openReports = this.openReports.filter((g) => g !== group);
    this.closedReports = [group, ...this.closedReports];
  }
}
